import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import ContentSlider from '../ui/ContentSlider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Events List Section - Fetches and displays events from the database
export function EventsListSection({ content, style }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderAnimated = content.sliderAnimated !== false;
  const autoplayMs = Number(content.sliderAutoplayMs) > 0 ? Number(content.sliderAutoplayMs) : 3000;
  const transitionMs = Number(content.sliderTransitionMs) >= 0 ? Number(content.sliderTransitionMs) : 380;

  const getEventStartDate = (event) => {
    const datePart = event?.date || "";
    const timePart = event?.time || "00:00";
    const date = new Date(`${datePart}T${timePart}`);
    return Number.isNaN(date.getTime()) ? new Date(0) : date;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${API_URL}/api/events`);
        if (res.ok) {
          let data = await res.json();
          
          // Filter past events if needed
          if (!content.showPastEvents) {
            const today = new Date().toISOString().split('T')[0];
            data = data.filter(event => event.date >= today);
          }

          data = [...data].sort((a, b) => getEventStartDate(a) - getEventStartDate(b));
          
          // Limit number of items
          const maxItems = Number(content.maxItems) > 0 ? Number(content.maxItems) : null;

          if (maxItems) {
            data = data.slice(0, maxItems);
          }
          
          setEvents(data);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [content.showPastEvents, content.maxItems]);

  if (loading) {
    return (
      <section className="py-16 px-6" style={style}>
        <div className="container mx-auto text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16 px-6 overflow-x-hidden"
      style={{
        backgroundColor: style?.backgroundColor || 'transparent',
        ...style
      }}
    >
      <div
        className={content.layout === 'list'
          ? 'container mx-auto'
          : 'relative left-1/2 w-screen -translate-x-1/2'}
        style={content.layout === 'list' ? { maxWidth: style?.maxWidth || '1200px' } : undefined}
      >
        {content.title && (
          <h2 className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] text-center mb-4">
            {content.title}
          </h2>
        )}
        
        {content.subtitle && (
          <p className="text-white/60 text-center text-lg mb-12 max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        )}

        {events.length === 0 ? (
          <p className="text-center text-white/60">Nenhum evento encontrado.</p>
        ) : content.layout === 'list' ? (
          <div className="space-y-4">
            {events.map((event) => (
              <EventListItem key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <ContentSlider
            items={events}
            animated={sliderAnimated}
            autoplayMs={autoplayMs}
            transitionMs={transitionMs}
            breakpoints={{ mobile: 1, tablet: 2, desktop: 4 }}
            getItemKey={(event, index) => `${event.id}-${index}`}
            renderItem={(event) => <EventCard event={event} />}
          />
        )}

        <div className="text-center mt-8">
          <Link to="/eventos">
            <Button variant="outline" className="border-[#fccf1f] text-[#fccf1f] hover:bg-[#fccf1f]/10">
              Ver Todos os Eventos
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function EventCard({ event }) {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl overflow-hidden hover:border-[#fccf1f]/50 transition-all duration-300 group">
      {event.image_url && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          
          {event.price && (
            <div className="absolute bottom-4 right-4 bg-[#fccf1f] text-black font-bold px-3 py-1 rounded-full text-sm">
              R$ {event.price.toFixed(2)}
            </div>
          )}
        </div>
      )}
      
      <div className="p-6">
        <h3 className="font-nunito font-bold text-xl text-white mb-3 group-hover:text-[#fccf1f] transition-colors">
          {event.title}
        </h3>
        
        <div className="space-y-2 text-white/60 text-sm mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#fccf1f]" />
            <span>{formatDate(event.date)}</span>
          </div>
          
          {event.time && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#fccf1f]" />
              <span>{event.time}</span>
            </div>
          )}
          
          {event.location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#fccf1f]" />
              <span>{event.location}</span>
            </div>
          )}
        </div>
        
        {event.description && (
          <p className="text-white/70 text-sm line-clamp-2 mb-4">
            {event.description}
          </p>
        )}
        
        <Link to={`/eventos?booking=${event.id}`}>
          <Button className="w-full bg-[#fccf1f] text-black hover:bg-[#fccf1f]/90 font-bold">
            Comprar Ingresso
          </Button>
        </Link>
      </div>
    </div>
  );
}

function EventListItem({ event }) {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <div className="flex items-center gap-6 p-4 bg-white/5 border border-white/10 rounded-lg hover:border-[#fccf1f]/50 transition-colors">
      <div className="text-center min-w-[60px]">
        <div className="text-[#fccf1f] font-bold text-2xl">
          {formatDate(event.date).split(' ')[0]}
        </div>
        <div className="text-white/60 text-sm uppercase">
          {formatDate(event.date).split(' ')[1]}
        </div>
      </div>
      
      <div className="flex-1">
        <h3 className="font-bold text-white">{event.title}</h3>
        <p className="text-white/60 text-sm">
          {event.time} • {event.location}
        </p>
      </div>
      
      {event.price && (
        <div className="text-[#fccf1f] font-bold">
          R$ {event.price.toFixed(2)}
        </div>
      )}
      
      <Link to={`/eventos?booking=${event.id}`}>
        <Button size="sm" className="bg-[#fccf1f] text-black hover:bg-[#fccf1f]/90">
          Comprar
        </Button>
      </Link>
    </div>
  );
}

// Games List Section - Fetches and displays games from the database
export function GamesListSection({ content, style }) {
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gamesRes, categoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/quiz/games`),
          fetch(`${API_URL}/api/quiz/categories`)
        ]);
        
        if (gamesRes.ok) {
          let gamesData = await gamesRes.json();
          
          // Filter by category if specified
          if (content.categoryFilter) {
            gamesData = gamesData.filter(game => game.category_id === content.categoryFilter);
          }
          
          // Limit number of items
          if (content.maxItems) {
            gamesData = gamesData.slice(0, content.maxItems);
          }
          
          setGames(gamesData);
        }
        
        if (categoriesRes.ok) {
          setCategories(await categoriesRes.json());
        }
      } catch (error) {
        console.error('Error fetching games:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [content.categoryFilter, content.maxItems]);

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Geral';
  };

  if (loading) {
    return (
      <section className="py-16 px-6" style={style}>
        <div className="container mx-auto text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16 px-6"
      style={{
        backgroundColor: style?.backgroundColor || 'transparent',
        ...style
      }}
    >
      <div className="container mx-auto" style={{ maxWidth: style?.maxWidth || '1200px' }}>
        {content.title && (
          <h2 className="font-nunito font-bold text-3xl md:text-4xl text-[#fccf1f] text-center mb-4">
            {content.title}
          </h2>
        )}
        
        {content.subtitle && (
          <p className="text-white/60 text-center text-lg mb-12 max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        )}

        {games.length === 0 ? (
          <p className="text-center text-white/60">Nenhum jogo encontrado.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <GameCard key={game.id} game={game} categoryName={getCategoryName(game.category_id)} />
            ))}
          </div>
        )}

        <div className="text-center mt-8">
          <Link to="/jogos">
            <Button variant="outline" className="border-[#fccf1f] text-[#fccf1f] hover:bg-[#fccf1f]/10">
              Ver Todos os Jogos
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function GameCard({ game, categoryName }) {
  return (
    <Link to={`/jogos?game=${game.id}`}>
      <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl overflow-hidden hover:border-[#fccf1f]/50 transition-all duration-300 group h-full">
        {game.image_url && (
          <div className="relative h-48 overflow-hidden">
            <img
              src={game.image_url}
              alt={game.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            
            <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
              {categoryName}
            </div>
          </div>
        )}
        
        <div className="p-6">
          <h3 className="font-nunito font-bold text-xl text-white mb-2 group-hover:text-[#fccf1f] transition-colors">
            {game.title}
          </h3>
          
          {game.description && (
            <p className="text-white/70 text-sm line-clamp-2 mb-4">
              {game.description}
            </p>
          )}
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/60">
              {game.questions_count || '10'} perguntas
            </span>
            <span className="text-[#fccf1f] font-medium">
              Jogar â†’
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
